import { FC } from "react";
import { Icon } from "@/components/Common/Icon";
import { Button } from "@/components/Base/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/Base/DropdownMenu";
import { IconNames } from "@/types/v2";
import { GroupByOption } from "../types";

export interface GroupBySelectorProps {
  groupBy: GroupByOption;
  onGroupByChange: (groupBy: GroupByOption) => void;
  disabled?: boolean;
}

const GROUP_BY_OPTIONS: Array<{
  value: GroupByOption;
  label: string;
  description: string;
  icon: IconNames;
}> = [
  {
    value: "none",
    label: "No Grouping",
    description: "Show all cards in a flat list",
    icon: "list",
  },
  {
    value: "container",
    label: "Group by Container",
    description: "Group cards by their current container",
    icon: "folderOpen",
  },
  {
    value: "technician",
    label: "Group by Technician",
    description: "Group cards by assigned technician",
    icon: "user",
  },
  {
    value: "serviceAdvisor",
    label: "Group by Service Advisor",
    description: "Group cards by service advisor",
    icon: "userCheck",
  },
];

export const GroupBySelector: FC<GroupBySelectorProps> = ({
  groupBy,
  onGroupByChange,
  disabled = false,
}) => {
  const currentOption = GROUP_BY_OPTIONS.find(
    (option) => option.value === groupBy
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className="h-8 gap-2"
        >
          <Icon name={currentOption?.icon || "list"} size={14} />
          <span className="hidden sm:inline">
            {currentOption?.label || "No Grouping"}
          </span>
          <span className="sm:hidden">Group</span>
          <Icon name="chevronDown" size={12} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        {GROUP_BY_OPTIONS.map((option, index) => (
          <div key={option.value}>
            <DropdownMenuItem
              onClick={() => onGroupByChange(option.value)}
              className="flex flex-col items-start gap-1 p-3"
            >
              <div className="flex items-center gap-2">
                <Icon name={option.icon} size={16} />
                <span className="font-medium">{option.label}</span>
                {groupBy === option.value && (
                  <Icon
                    name="check"
                    size={14}
                    className="ml-auto text-primary"
                  />
                )}
              </div>
              <span className="ml-6 text-xs text-muted-foreground">
                {option.description}
              </span>
            </DropdownMenuItem>
            {index < GROUP_BY_OPTIONS.length - 1 && <DropdownMenuSeparator />}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
